import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Train, Search, Calendar, Users, MapPin, Clock, ArrowRight,
  Loader2, ChevronRight, AlertCircle, CheckCircle, Star,
  Armchair, BadgeIndianRupee, RefreshCw, History, X,
  ArrowLeftRight, Filter, Zap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Train Class badges
const CLASS_COLORS = {
  '1A': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  '2A': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  '3A': 'bg-green-500/20 text-green-400 border-green-500/30',
  '3E': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  'SL': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'CC': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  '2S': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'GN': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

// Availability status colors
const STATUS_COLORS = {
  'AVAILABLE': 'text-green-400',
  'RAC': 'text-yellow-400',
  'WL': 'text-orange-400',
  'REGRET': 'text-red-400',
};

export default function TrainBookingPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Search form state
  const [fromStation, setFromStation] = useState('');
  const [toStation, setToStation] = useState('');
  const [journeyDate, setJourneyDate] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedQuota, setSelectedQuota] = useState('GN');
  
  // Data states
  const [trains, setTrains] = useState([]);
  const [classes, setClasses] = useState([]);
  const [quotas, setQuotas] = useState([]);
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [stationSuggestions, setStationSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search'); // search, pnr
  const [selectedTrain, setSelectedTrain] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  
  // PNR state
  const [pnrNumber, setPnrNumber] = useState('');
  const [pnrStatus, setPnrStatus] = useState(null);
  const [checkingPnr, setCheckingPnr] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch classes
      try {
        const classRes = await fetch(`${API_BASE_URL}/api/trains/classes`);
        if (classRes.ok) {
          const data = await classRes.json();
          setClasses(data.classes || []);
        }
      } catch (e) {
        console.error('Failed to fetch classes:', e);
      }
      
      // Fetch quotas
      try {
        const quotaRes = await fetch(`${API_BASE_URL}/api/trains/quotas`);
        if (quotaRes.ok) {
          const data = await quotaRes.json();
          setQuotas(data.quotas || []);
        }
      } catch (e) {
        console.error('Failed to fetch quotas:', e);
      }
      
      // Fetch popular routes
      try {
        const routeRes = await fetch(`${API_BASE_URL}/api/trains/popular-routes`);
        if (routeRes.ok) {
          const data = await routeRes.json();
          setPopularRoutes(data.routes || []);
        }
      } catch (e) {
        console.error('Failed to fetch popular routes:', e);
      }
    };
    
    loadInitialData();
    
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setJourneyDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  const fetchStationSuggestions = async (query, type) => {
    if (query.length < 2) {
      setStationSuggestions([]);
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/trains/stations?query=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setStationSuggestions(data.stations || []);
        if (type === 'from') setShowFromSuggestions(true);
        else setShowToSuggestions(true);
      }
    } catch (e) {
      console.error('Station suggestion error:', e);
    }
  };

  const handleSearch = async () => {
    if (!fromStation || !toStation || !journeyDate) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setIsLoading(true);
    setTrains([]);
    setSelectedTrain(null);
    setAvailability(null);
    
    try {
      const params = new URLSearchParams({
        from_station: fromStation,
        to_station: toStation,
        journey_date: journeyDate,
        quota: selectedQuota
      });
      
      if (selectedClass) {
        params.append('travel_class', selectedClass);
      }
      
      const res = await fetch(`${API_BASE_URL}/api/trains/search?${params}`);
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setTrains(data.trains || []);
          if (data.demo_mode) {
            toast.info('Showing demo trains. Live data requires API activation.');
          }
        } else {
          toast.error('No trains found');
        }
      } else {
        toast.error('Search failed');
      }
    } catch (e) {
      toast.error('Failed to search trains');
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckAvailability = async (train, travelClass) => {
    setCheckingAvailability(true);
    setSelectedTrain(train);
    
    try {
      const params = new URLSearchParams({
        train_number: train.train_number,
        from_station: fromStation,
        to_station: toStation,
        journey_date: journeyDate,
        travel_class: travelClass,
        quota: selectedQuota
      });
      
      const res = await fetch(`${API_BASE_URL}/api/trains/availability?${params}`);
      
      if (res.ok) {
        const data = await res.json();
        setAvailability(data);
      }
    } catch (e) {
      toast.error('Failed to check availability');
      console.error(e);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleCheckPnr = async () => {
    if (!pnrNumber || pnrNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit PNR number');
      return;
    }
    
    setCheckingPnr(true);
    setPnrStatus(null);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/trains/pnr/${pnrNumber}`);
      
      if (res.ok) {
        const data = await res.json();
        setPnrStatus(data);
        if (data.demo_mode) {
          toast.info('Showing demo PNR status');
        }
      } else {
        toast.error('PNR not found');
      }
    } catch (e) {
      toast.error('Failed to check PNR status');
      console.error(e);
    } finally {
      setCheckingPnr(false);
    }
  };

  const swapStations = () => {
    const temp = fromStation;
    setFromStation(toStation);
    setToStation(temp);
  };

  const selectPopularRoute = (route) => {
    setFromStation(route.from);
    setToStation(route.to);
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-indigo-600/10 blur-[150px]" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Train className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                AIRA Trains
              </h1>
              <p className="text-xs text-gray-500">Indian Railways Booking</p>
            </div>
          </motion.div>
          
          {/* Tab Navigation */}
          <div className="flex items-center gap-2 bg-white/5 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('search')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'search'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              <Search className="w-4 h-4" />
              Search Trains
            </button>
            <button
              onClick={() => setActiveTab('pnr')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'pnr'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              <History className="w-4 h-4" />
              PNR Status
            </button>
          </div>
        </header>
        
        <AnimatePresence mode="wait">
          {/* Search Tab */}
          {activeTab === 'search' && (
            <motion.div
              key="search"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Search Form */}
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-2xl blur-xl" />
                <div className="relative p-6 bg-[#12141A]/80 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* From Station */}
                    <div className="md:col-span-3 relative">
                      <label className="block text-xs text-gray-400 mb-1">From</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text"
                          value={fromStation}
                          onChange={(e) => {
                            setFromStation(e.target.value.toUpperCase());
                            fetchStationSuggestions(e.target.value, 'from');
                          }}
                          onFocus={() => fromStation.length >= 2 && setShowFromSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowFromSuggestions(false), 200)}
                          placeholder="Station code (e.g. NDLS)"
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-blue-500/50 outline-none"
                        />
                        {showFromSuggestions && stationSuggestions.length > 0 && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1a1c24] border border-white/10 rounded-xl overflow-hidden">
                            {stationSuggestions.map((s) => (
                              <button
                                key={s.code}
                                onClick={() => {
                                  setFromStation(s.code);
                                  setShowFromSuggestions(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                              >
                                <span className="text-white">{s.code}</span>
                                <span className="text-gray-500 ml-2">{s.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Swap Button */}
                    <div className="md:col-span-1 flex items-end justify-center pb-1">
                      <button
                        onClick={swapStations}
                        className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <ArrowLeftRight className="w-5 h-5 text-blue-400" />
                      </button>
                    </div>
                    
                    {/* To Station */}
                    <div className="md:col-span-3 relative">
                      <label className="block text-xs text-gray-400 mb-1">To</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text"
                          value={toStation}
                          onChange={(e) => {
                            setToStation(e.target.value.toUpperCase());
                            fetchStationSuggestions(e.target.value, 'to');
                          }}
                          onFocus={() => toStation.length >= 2 && setShowToSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowToSuggestions(false), 200)}
                          placeholder="Station code (e.g. BCT)"
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-blue-500/50 outline-none"
                        />
                        {showToSuggestions && stationSuggestions.length > 0 && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1a1c24] border border-white/10 rounded-xl overflow-hidden">
                            {stationSuggestions.map((s) => (
                              <button
                                key={s.code}
                                onClick={() => {
                                  setToStation(s.code);
                                  setShowToSuggestions(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                              >
                                <span className="text-white">{s.code}</span>
                                <span className="text-gray-500 ml-2">{s.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Date */}
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-1">Journey Date</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="date"
                          value={journeyDate}
                          onChange={(e) => setJourneyDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-blue-500/50 outline-none"
                        />
                      </div>
                    </div>
                    
                    {/* Quota */}
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-1">Quota</label>
                      <select
                        value={selectedQuota}
                        onChange={(e) => setSelectedQuota(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-blue-500/50 outline-none"
                      >
                        {quotas.map(q => (
                          <option key={q.code} value={q.code}>{q.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Search Button */}
                    <div className="md:col-span-1 flex items-end">
                      <button
                        onClick={handleSearch}
                        disabled={isLoading}
                        className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl text-white font-semibold hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Search className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Popular Routes */}
                  {trains.length === 0 && (
                    <div className="mt-6 pt-4 border-t border-white/10">
                      <p className="text-xs text-gray-500 mb-3">Popular Routes</p>
                      <div className="flex flex-wrap gap-2">
                        {popularRoutes.map((route) => (
                          <button
                            key={`${route.from}-${route.to}`}
                            onClick={() => selectPopularRoute(route)}
                            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-xs text-gray-300 transition-colors"
                          >
                            {route.from_name} → {route.to_name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Train Results */}
              {trains.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      {trains.length} Trains Found
                    </h2>
                    <span className="text-sm text-gray-500">
                      {fromStation} → {toStation} | {new Date(journeyDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  
                  {trains.map((train, index) => (
                    <motion.div
                      key={train.train_number}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 bg-[#12141A]/60 backdrop-blur-xl border border-white/10 rounded-2xl hover:border-blue-500/30 transition-all"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Train Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-blue-400 font-mono">{train.train_number}</span>
                            <span className="text-white font-semibold">{train.train_name}</span>
                            {train.train_type && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                {train.train_type}
                              </span>
                            )}
                          </div>
                          
                          {/* Timing */}
                          <div className="flex items-center gap-4 text-sm">
                            <div>
                              <p className="text-xl font-bold text-white">{train.departure}</p>
                              <p className="text-gray-500">{train.from_station || fromStation}</p>
                            </div>
                            <div className="flex-1 flex items-center gap-2 px-4">
                              <div className="flex-1 h-px bg-white/20" />
                              <span className="text-xs text-gray-500">{train.duration}</span>
                              <div className="flex-1 h-px bg-white/20" />
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-white">{train.arrival}</p>
                              <p className="text-gray-500">{train.to_station || toStation}</p>
                            </div>
                          </div>
                          
                          {/* Run Days */}
                          {train.run_days && train.run_days.length > 0 && (
                            <div className="mt-2 flex items-center gap-1">
                              <span className="text-xs text-gray-500">Runs on:</span>
                              {[
                                { short: 'S', full: 'Sun' },
                                { short: 'M', full: 'Mon' },
                                { short: 'T', full: 'Tue' },
                                { short: 'W', full: 'Wed' },
                                { short: 'T', full: 'Thu' },
                                { short: 'F', full: 'Fri' },
                                { short: 'S', full: 'Sat' }
                              ].map((day) => (
                                <span
                                  key={day.full}
                                  className={`w-5 h-5 flex items-center justify-center text-xs rounded ${
                                    train.run_days.includes(day.full)
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-white/5 text-gray-600'
                                  }`}
                                >
                                  {day.short}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Classes */}
                        <div className="flex flex-wrap gap-2">
                          {train.classes && train.classes.map(cls => (
                            <button
                              key={cls}
                              onClick={() => handleCheckAvailability(train, cls)}
                              disabled={checkingAvailability && selectedTrain?.train_number === train.train_number}
                              className={`px-3 py-2 border rounded-xl text-sm font-medium transition-all hover:scale-105 ${CLASS_COLORS[cls] || 'bg-white/10 text-white border-white/20'}`}
                            >
                              {checkingAvailability && selectedTrain?.train_number === train.train_number ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                cls
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Availability Details */}
                      {selectedTrain?.train_number === train.train_number && availability && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-4 pt-4 border-t border-white/10"
                        >
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-gray-400">Class:</span>
                                <span className="font-semibold">{availability.availability?.class_name}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`text-lg font-bold ${STATUS_COLORS[availability.availability?.status] || 'text-white'}`}>
                                  {availability.availability?.status}
                                  {availability.availability?.status === 'AVAILABLE' && ` (${availability.availability?.available_seats} seats)`}
                                  {availability.availability?.status === 'RAC' && ` ${availability.availability?.rac_count}`}
                                  {availability.availability?.status === 'WL' && ` ${availability.availability?.waitlist_count}`}
                                </span>
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <p className="text-2xl font-bold text-white">
                                ₹{availability.availability?.total_fare?.toLocaleString()}
                              </p>
                              <p className="text-xs text-gray-500">per person</p>
                            </div>
                            
                            <button
                              onClick={() => toast.info('Booking feature coming soon!')}
                              className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white font-semibold hover:opacity-90 transition-all"
                            >
                              Book Now
                            </button>
                          </div>
                          
                          {availability.demo_mode && (
                            <p className="mt-2 text-xs text-yellow-500/70 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Demo data - Live availability requires API activation
                            </p>
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
              
              {/* No Results */}
              {!isLoading && trains.length === 0 && fromStation && toStation && (
                <div className="text-center py-16">
                  <Train className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Search for Trains</h3>
                  <p className="text-gray-400">Enter stations and date to find available trains</p>
                </div>
              )}
            </motion.div>
          )}
          
          {/* PNR Status Tab */}
          {activeTab === 'pnr' && (
            <motion.div
              key="pnr"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-2xl mx-auto"
            >
              <div className="p-6 bg-[#12141A]/80 backdrop-blur-xl border border-white/10 rounded-2xl">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" />
                  Check PNR Status
                </h2>
                
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={pnrNumber}
                    onChange={(e) => setPnrNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter 10-digit PNR number"
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-blue-500/50 outline-none font-mono text-lg tracking-wider"
                    maxLength={10}
                  />
                  <button
                    onClick={handleCheckPnr}
                    disabled={checkingPnr || pnrNumber.length !== 10}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl text-white font-semibold hover:opacity-90 disabled:opacity-50 transition-all flex items-center gap-2"
                  >
                    {checkingPnr ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        Check
                      </>
                    )}
                  </button>
                </div>
                
                {/* PNR Result */}
                {pnrStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 bg-white/5 rounded-xl"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-500">PNR Number</p>
                        <p className="text-xl font-mono font-bold text-white">{pnrStatus.pnr_data?.pnr}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Chart Status</p>
                        <p className="text-white font-medium">{pnrStatus.pnr_data?.chart_status}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Train</p>
                        <p className="text-white">{pnrStatus.pnr_data?.train_number} - {pnrStatus.pnr_data?.train_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Journey Date</p>
                        <p className="text-white">{pnrStatus.pnr_data?.journey_date}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">From - To</p>
                        <p className="text-white">{pnrStatus.pnr_data?.from_station} → {pnrStatus.pnr_data?.to_station}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Class</p>
                        <p className="text-white">{pnrStatus.pnr_data?.class}</p>
                      </div>
                    </div>
                    
                    {/* Passengers */}
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-sm text-gray-500 mb-2">Passengers</p>
                      <div className="space-y-2">
                        {pnrStatus.pnr_data?.passengers?.map((p) => (
                          <div key={`passenger-${p.number}`} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                            <span className="text-white">Passenger {p.number}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-gray-400">Booking: {p.booking_status}</span>
                              <span className="text-green-400 font-semibold">Current: {p.current_status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {pnrStatus.demo_mode && (
                      <p className="mt-4 text-xs text-yellow-500/70 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {pnrStatus.message}
                      </p>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
